import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraphQLClient } from 'graphql-request';

import { getPublicConfig } from '@sola/config/lib/getters.js';

import { graphql } from '../gql/gql';
import {
  IndexInput,
  QueryResponsePrivate,
  QueryResponsePublished,
  SearchResultsInput,
} from './schema';

// @TODO: Move all this logic to a provider, etc.
const getGraphQLClient = () => {
  // TODO secrets

  const {
    NEXT_PUBLIC_SKIPPY_SEARCH_API_URL,
    NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY,
  } = getPublicConfig();

  if (
    !NEXT_PUBLIC_SKIPPY_SEARCH_API_URL ||
    !NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY
  ) {
    throw new Error(
      'Missing environment variable for SKIPPY_SEARCH_API_URL or API_KEY. Please confirm your environment (Vite or Next) set it in your .env file.',
    );
  }

  return new GraphQLClient(NEXT_PUBLIC_SKIPPY_SEARCH_API_URL, {
    headers: {
      'x-api-key': NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY,
    } as Record<string, string>,
  });
};

const queryKeys = {
  listSearchResults: 'listSearchResults',
  listArticles: 'listArticles',
  listRelatedArticles: 'listRelatedArticles',
  listCatalog: 'listCatalog',
  listPrivateSearchResults: 'listPrivateSearchResults',
};

const indexEntryMutation = graphql(`
  mutation indexEntry($input: IndexInput!) {
    indexEntry(input: $input) {
      success
      error
    }
  }
`);

const indexPrivateEntryMutation = graphql(`
  mutation indexPrivateEntry($input: IndexInput!) {
    indexPrivateEntry(input: $input) {
      success
      error
    }
  }
`);

export const publishedFieldsFragment = graphql(`
  fragment PublishedFields on PublishedEntry {
    contentfulEntryID
    contentfulType
    pageTitle
    summary
    path
    media {
      image {
        url
        alt
      }
      video {
        url
      }
      caption
    }
    contributors {
      name
      position
      image {
        url
      }
    }
    template
    fund {
      id
      tcmId
      fundTitle
      fundCodeOneTime
      fundCodeCommitment
      catalogAmount
    }
    taxonomy {
      articleType
      articleTopics
      articleThemes
      fundPromotion
      fundCategory
      programCountry
      programRegion
      other
    }
    datePublished
    dateModified
    dateIndexed
  }
`);

export const privateFieldsFragment = graphql(`
  fragment PrivateFields on PrivateEntry {
    contentfulEntryID
    contentfulType
    pageTitle
    summary
    path
    isReferenced
    status
    dateModified
    dateIndexed
  }
`);

const listSearchResultsQuery = graphql(`
  query listSearchResults($input: SearchResultsInput!) {
    listSearchResults(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);

const listArticlesQuery = graphql(`
  query listArticles($input: SearchResultsInput!) {
    listArticles(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      totalCount
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);

const listRelatedArticlesQuery = graphql(`
  query listRelatedArticles($input: SearchResultsInput!) {
    listRelatedArticles(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      totalCount
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);

const listCatalogQuery = graphql(`
  query listCatalog($input: SearchResultsInput!) {
    listCatalog(input: $input) {
      totalCount
      items {
        ...PublishedFields
      }
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);

const listPrivateSearchResultsQuery = graphql(`
  query listPrivateSearchResults($input: SearchResultsInput!) {
    listPrivateSearchResults(input: $input) {
      totalCount
      items {
        ...PrivateFields
      }
      facets {
        field
        values {
          key
          count
        }
      }
    }
  }
`);

export const getListSearchResultsQuery = async (input: SearchResultsInput) => {
  const graphQLClient = getGraphQLClient();
  const response = await graphQLClient.request<{
    listSearchResults: QueryResponsePublished;
  }>(listSearchResultsQuery, {
    input,
  });
  return QueryResponsePublished.parse(response.listSearchResults);
};

export const useListArticlesQuery = (input: SearchResultsInput) => {
  const graphQLClient = getGraphQLClient();
  return useQuery({
    queryKey: [queryKeys.listArticles, input],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        listArticles: QueryResponsePublished;
      }>(listArticlesQuery, {
        input,
      });
      return QueryResponsePublished.parse(response.listArticles);
    },
  });
};

export const useListRelatedArticlesQuery = (input: SearchResultsInput) => {
  const graphQLClient = getGraphQLClient();
  return useQuery({
    queryKey: [queryKeys.listRelatedArticles, input],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        listRelatedArticles: QueryResponsePublished;
      }>(listRelatedArticlesQuery, {
        input,
      });
      return QueryResponsePublished.parse(response.listRelatedArticles);
    },
  });
};

export const useListCatalogQuery = (input: SearchResultsInput) => {
  const graphQLClient = getGraphQLClient();
  return useQuery({
    queryKey: [queryKeys.listCatalog, input],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        listCatalog: QueryResponsePublished;
      }>(listCatalogQuery, {
        input,
      });
      return QueryResponsePublished.parse(response.listCatalog);
    },
  });
};

export const useListPrivateSearchResultsQuery = (input: SearchResultsInput) => {
  const graphQLClient = getGraphQLClient();
  return useQuery({
    queryKey: [queryKeys.listPrivateSearchResults, input],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        listPrivateSearchResults: QueryResponsePrivate;
      }>(listPrivateSearchResultsQuery, {
        input,
      });
      return QueryResponsePrivate.parse(response.listPrivateSearchResults);
    },
  });
};

export const useIndexEntryMutation = () => {
  const queryClient = useQueryClient();
  const graphQLClient = getGraphQLClient();
  return useMutation({
    mutationFn: async (input: IndexInput) =>
      graphQLClient.request(indexEntryMutation, { input }),
    onSuccess: () => {
      // @TODO: Does this work with pagination?
      queryClient.invalidateQueries({
        queryKey: [queryKeys.listSearchResults],
      });
    },
  });
};

export const useIndexPrivateEntryMutation = () => {
  const queryClient = useQueryClient();
  const graphQLClient = getGraphQLClient();
  return useMutation({
    mutationFn: async (input: IndexInput) =>
      graphQLClient.request(indexPrivateEntryMutation, { input }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.listPrivateSearchResults],
      });
    },
  });
};
